/**
 * OutgoingShiftBanner
 * CCH Care — Phase 1 of multi-shift handoff support
 *
 * Path: src/components/layout/OutgoingShiftBanner.jsx
 *
 * Renders an amber strip below the header when useShift exposes an
 * outgoingShift — i.e., the user has a shift currently in its grace window
 * in addition to their wall-clock-current shift.
 *
 * Two variants:
 *   - NOT in outgoing mode: CTA strip. Now count-aware (2026-06-16):
 *       - count > 0  → "3 residents still need documentation on Day Shift…"
 *       - count == 0 → suppressed entirely (no false nag when they're done)
 *       - count unknown (null/undefined — loading or fetch error) → generic
 *         "Unfinished documentation…" text, so the affordance is never lost
 *     Whole strip is clickable and switches into outgoing mode.
 *   - IN outgoing mode: confirmation strip ("📋 Working in Day Shift
 *     (outgoing) — 1h 27m grace left. ← Return to current shift"). Button
 *     on the right exits outgoing mode. Always shown regardless of count
 *     (it communicates mode, not a to-do).
 *
 * The incomplete count is read from useShift (outgoingIncompleteCount),
 * computed there by reusing residents/assigned.php against the outgoing
 * shift — so it matches the dashboard exactly. Reading it from context
 * (rather than threading a new prop) keeps the parent render call
 * unchanged and follows the 2026-05-30 single-source-of-truth refactor.
 *
 * Color theme matches GraceWindowBanner so the user reads "grace context"
 * consistently across both banners. When in outgoing mode both banners will
 * be visible — that's intentional: OutgoingShiftBanner communicates mode,
 * GraceWindowBanner communicates the time-bound documentation deadline.
 */
import useShift from '../../hooks/useShift';

const AMBER_BG     = '#FEF3C7';
const AMBER_BORDER = '#F59E0B';
const AMBER_TEXT   = '#92400E';

function formatGrace(mins) {
  const safe = Math.max(0, mins ?? 0);
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function shiftLabel(s) {
  if (!s) return 'Previous Shift';
  if (s.shift_name) {
    // shift_name may or may not already carry the word "Shift":
    // Riverside seeds bare "Day"/"Evening"/"Night", but register.php
    // seeds facility-2 as "Day Shift"/"Evening Shift"/"Night Shift".
    // Strip a trailing "Shift" before re-appending so we never render
    // "Day Shift Shift".
    const base = s.shift_name.replace(/\s*shift\s*$/i, '').trim();
    return base ? `${base} Shift` : s.shift_name;
  }
  return ({
    AM:  'Day Shift',
    PM:  'Evening Shift',
    NOC: 'Night Shift',
  })[s.shift_type] ?? 'Previous Shift';
}

export default function OutgoingShiftBanner({
  outgoingShift,
  isOutgoingMode,
  onEnter,
  onExit,
}) {
  const { outgoingIncompleteCount } = useShift();

  if (!outgoingShift) return null;

  const graceDisplay = formatGrace(outgoingShift.grace_minutes_remaining);
  const label        = shiftLabel(outgoingShift);

  const containerBase = {
    background: AMBER_BG,
    borderBottom: `1px solid ${AMBER_BORDER}`,
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    fontSize: 13,
    fontWeight: 600,
    color: AMBER_TEXT,
  };

  if (isOutgoingMode) {
    // Confirmation strip — always shown while in outgoing mode.
    return (
      <div style={containerBase}>
        <span>
          📋 Working in <strong>{label}</strong> (outgoing) — {graceDisplay} grace left
        </span>
        <button
          onClick={onExit}
          style={{
            background: '#fff',
            border: `1px solid ${AMBER_BORDER}`,
            color: AMBER_TEXT,
            padding: '6px 14px',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontFamily: 'inherit',
          }}
        >
          ← Return to current shift
        </button>
      </div>
    );
  }

  // CTA strip (not in outgoing mode) — count-aware.
  // Known-zero → suppress (the trust win: don't nag a CNA who's finished).
  if (outgoingIncompleteCount === 0) return null;

  // Build the message: use the resident count when we know it (> 0), and
  // fall back to the generic message when it's unknown so we never lose
  // the affordance on a slow/failed count fetch.
  const hasCount = typeof outgoingIncompleteCount === 'number' && outgoingIncompleteCount > 0;
  const noun     = outgoingIncompleteCount === 1 ? 'resident' : 'residents';
  const verb     = outgoingIncompleteCount === 1 ? 'needs' : 'need';

  const message = hasCount
    ? `⏱ ${outgoingIncompleteCount} ${noun} ${verb} documentation on ${label} — ${graceDisplay} left in grace`
    : `⏱ Unfinished documentation on ${label} — ${graceDisplay} left in grace`;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onEnter}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEnter();
        }
      }}
      style={{ ...containerBase, cursor: 'pointer' }}
    >
      <span>{message}</span>
      <span style={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
        Continue →
      </span>
    </div>
  );
}